import React, { useEffect, useRef, useState } from "react";

/**
 * Gemeinsame Modal-Motion:
 * - Enter: wie bisher
 * - Exit: kurzer, subtiler Fade-Out
 * - hält beim Schließen kurz gemountet, damit Exit sichtbar bleibt
 * - merkt sich letzte Children, damit Content beim Close nicht abrupt verschwindet
 */
const EXIT_DURATION_MS = 145;

export default function ModalMotionShell({
  isOpen,
  onClose,
  children,
  zIndexClass = "z-[200]",
  backdropTintClass = "bg-black/60",
  stretch = false,
  backdropClassName = "",
  surfaceClassName = "",
  role = "dialog",
  ariaModal = true,
  onExited,
}) {
  const [isRendered, setIsRendered] = useState(isOpen);
  const [phase, setPhase] = useState(isOpen ? "enter" : "idle");

  const exitTimerRef = useRef(null);
  const rafRef = useRef(null);
  const lastChildrenRef = useRef(children);
  const latestOnExitedRef = useRef(onExited);

  if (children != null) {
    lastChildrenRef.current = children;
  }

  useEffect(() => {
    latestOnExitedRef.current = onExited;
  }, [onExited]);

  useEffect(() => {
    if (typeof window === "undefined") {
      setIsRendered(isOpen);
      setPhase(isOpen ? "enter" : "idle");
      return;
    }

    if (exitTimerRef.current) {
      window.clearTimeout(exitTimerRef.current);
      exitTimerRef.current = null;
    }

    if (rafRef.current) {
      window.cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }

    if (isOpen) {
      setIsRendered(true);
      rafRef.current = window.requestAnimationFrame(() => {
        setPhase("enter");
      });
      return;
    }

    if (!isRendered) {
      setPhase("idle");
      return;
    }

    setPhase("exit");

    exitTimerRef.current = window.setTimeout(() => {
      setIsRendered(false);
      setPhase("idle");
      exitTimerRef.current = null;
      latestOnExitedRef.current?.();
    }, EXIT_DURATION_MS);

    return () => {
      if (exitTimerRef.current) {
        window.clearTimeout(exitTimerRef.current);
        exitTimerRef.current = null;
      }
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isOpen, isRendered]);

  useEffect(() => {
    return () => {
      if (typeof window !== "undefined") {
        if (exitTimerRef.current) window.clearTimeout(exitTimerRef.current);
        if (rafRef.current) window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);

  if (!isRendered) return null;

  const layoutClasses = stretch
    ? "items-stretch justify-center p-0"
    : "items-center justify-center px-2 py-2 md:px-3 md:py-2";

  const backdropMotionClass =
    phase === "exit" ? "modal-backdrop-exit" : "modal-backdrop-enter";

  const surfaceMotionClass =
    phase === "exit" ? "modal-surface-exit" : "modal-surface-enter";

  const content = children ?? lastChildrenRef.current;

  return (
    <div
      className={`${backdropMotionClass} fixed inset-0 ${zIndexClass} ${backdropTintClass} backdrop-blur-[2px] md:backdrop-blur-[3px] backdrop-saturate-75 flex ${layoutClasses} ${backdropClassName}`.trim()}
      onClick={(e) => {
        if (e.target === e.currentTarget && isOpen) {
          onClose?.();
        }
      }}
      role={role}
      aria-modal={ariaModal}
    >
      <div
        className={`${surfaceMotionClass} flex w-full max-w-full justify-center min-w-0 ${surfaceClassName}`.trim()}
      >
        {content}
      </div>
    </div>
  );
}
